/**
 * Client-side helpers for two-phase strategems: the local animation can finish before
 * `strategem-initiated` arrives (delayed play-turn + network). Without a flush, the client
 * never sends `strategem-animation-complete` and the server may fall back (~1.5s).
 */

/** @param {*} strategemInstanceId */
export function normalizeStrategemInstanceKey(strategemInstanceId) {
  if (strategemInstanceId == null) return null;
  return String(strategemInstanceId);
}

/**
 * @param {Map} pendingMap
 * @param {*} strategemInstanceId
 */
export function getPendingStrategemAnimInfo(pendingMap, strategemInstanceId) {
  const key = normalizeStrategemInstanceKey(strategemInstanceId);
  if (!key) return null;
  return pendingMap.get(key) ?? null;
}

/**
 * @param {Map} pendingMap
 * @param {*} strategemInstanceId
 * @param {object} info
 */
export function setPendingStrategemAnimInfo(pendingMap, strategemInstanceId, info) {
  const key = normalizeStrategemInstanceKey(strategemInstanceId);
  if (!key) return;
  pendingMap.set(key, info);
}

/**
 * @param {Map} pendingMap
 * @param {*} strategemInstanceId
 */
export function deletePendingStrategemAnimInfo(pendingMap, strategemInstanceId) {
  const key = normalizeStrategemInstanceKey(strategemInstanceId);
  if (!key) return;
  pendingMap.delete(key);
}

/**
 * @param {object} p
 * @param {*} p.pendingAnimInfo - Entry from pendingStrategemAnimationIdsRef, if strategem-initiated already ran
 * @param {*} p.strategemInstanceId
 * @param {boolean} p.wsReady - WebSocket open and ready
 * @param {boolean} p.isSpectating
 * @param {boolean} p.isAutobattlerSpectator
 * @param {number} p.remainingAnimationsForStrategemCount - Other active animations for this cast (exclude current)
 * @returns {boolean} Whether to add strategemInstanceId to the deferred set
 */
export function shouldDeferStrategemAnimationComplete({
  pendingAnimInfo,
  strategemInstanceId,
  wsReady,
  isSpectating,
  isAutobattlerSpectator,
  remainingAnimationsForStrategemCount,
}) {
  if (pendingAnimInfo || strategemInstanceId == null || !wsReady || isSpectating || isAutobattlerSpectator) {
    return false;
  }
  return remainingAnimationsForStrategemCount === 0;
}

/**
 * When strategem-initiated arrives after the animation completed, send completion immediately.
 * Mutates `deferredSet` and `pendingMap` on success (same as BattleUI inline logic).
 *
 * @param {object} p
 * @param {Set<string>} p.deferredSet
 * @param {Map} p.pendingMap - strategemInstanceId -> { strategemAnimationId, ... }
 * @param {*} p.strategemInstanceId - Must match the key used when calling `pendingMap.set` from the server message
 * @param {boolean} p.wsReady
 * @param {boolean} p.isSpectating
 * @param {boolean} p.isAutobattlerSpectator
 * @returns {{ ok: false } | { ok: true, strategemAnimationId: string, strategemInstanceId: string }}
 */
export function tryFlushDeferredStrategemComplete({
  deferredSet,
  pendingMap,
  strategemInstanceId,
  wsReady,
  isSpectating,
  isAutobattlerSpectator,
}) {
  const deferredSid = normalizeStrategemInstanceKey(strategemInstanceId);
  if (
    !deferredSid ||
    !deferredSet.has(deferredSid) ||
    !wsReady ||
    isSpectating ||
    isAutobattlerSpectator
  ) {
    return { ok: false };
  }
  const pendingAnimInfo = getPendingStrategemAnimInfo(pendingMap, strategemInstanceId);
  if (!pendingAnimInfo) {
    return { ok: false };
  }
  deferredSet.delete(deferredSid);
  deletePendingStrategemAnimInfo(pendingMap, strategemInstanceId);
  return {
    ok: true,
    strategemAnimationId: pendingAnimInfo.strategemAnimationId,
    strategemInstanceId: deferredSid,
  };
}

/**
 * @param {object} p
 * @param {Map} p.pendingMap
 * @param {*} p.strategemInstanceId
 * @param {boolean} p.wsReady
 * @param {boolean} p.isSpectating
 * @param {boolean} p.isAutobattlerSpectator
 * @param {number} p.remainingAnimationsForStrategemCount
 * @returns {{ send: boolean, strategemAnimationId?: string, strategemInstanceId?: string }}
 */
export function resolveStrategemAnimationCompleteSend({
  pendingMap,
  strategemInstanceId,
  wsReady,
  isSpectating,
  isAutobattlerSpectator,
  remainingAnimationsForStrategemCount,
}) {
  const sid = normalizeStrategemInstanceKey(strategemInstanceId);
  if (!sid || !wsReady || isSpectating || isAutobattlerSpectator) {
    return { send: false };
  }
  const pendingAnimInfo = getPendingStrategemAnimInfo(pendingMap, sid);
  if (!pendingAnimInfo || remainingAnimationsForStrategemCount !== 0) {
    return { send: false, pendingAnimInfo };
  }
  return {
    send: true,
    strategemAnimationId: pendingAnimInfo.strategemAnimationId,
    strategemInstanceId: sid,
    pendingAnimInfo,
  };
}
